import type { z } from 'zod';
import { Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBNotesCategorySchema, NotesCategorySchema } from './schemas';

type DBNotesCategory = z.infer<typeof DBNotesCategorySchema>;
type NotesCategory = z.infer<typeof NotesCategorySchema>;

export const NotesCategoryCodecConfig: TransformerConfig<
  typeof NotesCategorySchema,
  typeof DBNotesCategorySchema
> = {
  name: 'NotesCategory',
  clientSchema: NotesCategorySchema,
  dbSchema: DBNotesCategorySchema,
  transformers: [
    // notes: [{ key, ... }] ↔ notes: [{ noteId: 'subject#category#key', key, ... }]
    // Uses dependencies for partial updates where only notes is provided
    createTransformer<typeof NotesCategorySchema, typeof DBNotesCategorySchema>({
      encoder: {
        sourceFields: ['notes'] as const,
        dependencies: ['notes'] as const,
        encode: ({ notes }) => ({
          notes: (notes as NotesCategory['notes']).map(note => ({
            ...note,
            noteId: `${note.subject}#${note.category}#${note.key}`,
          })),
        }),
      },
      decoder: {
        sourceFields: ['notes'] as const,
        decode: ({ notes }) => ({
          notes: (notes as DBNotesCategory['notes']).map(({ noteId, ...rest }) => ({
            subject: noteId.split('#')[0]! as Subject,
            category: noteId.split('#')[1]!,
            ...rest,
          })),
        }),
      },
    }),
  ],
};

export const encodeNotesCategoryToDb = createEncodeToDbFn(NotesCategoryCodecConfig);
export const decodeNotesCategoryFromDb = createDecodeFromDbFn(NotesCategoryCodecConfig);
